import { ClientEmployeeFileUploadFieldsFragment } from "@graphql/client-employee-file-uploads/clientEmployeeFileUploads.g";
import React from "react";

import { Table } from "@design-system/Table/Table";
import { Text } from "@design-system/Text/Text";

import { createTableConfiguration } from "@utils/table/TableConfigurationSystem";

import {
  CLIENT_EMPLOYEE_FILE_UPLOAD_TABLE_COLUMN_LABELS,
  ClientEmployeeFileUploadTableColumns,
  DEFAULT_CLIENT_EMPLOYEE_FILE_UPLOAD_TABLE_COLUMN_ORDER,
  DEFAULT_CLIENT_EMPLOYEE_FILE_UPLOAD_TABLE_COLUMN_SETTINGS,
} from "./UI/ClientEmployeeFileUploadsTableColumns";

export const DEFAULT_CLIENT_EMPLOYEE_FILE_UPLOADS_FILTER_VALUES = {
  fileNameSearch: "",
  mimetypeSearch: "",
};

export function formatBytes(bytes: number, decimals = 2): string {
  if (!+bytes) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

export const ClientEmployeeFileUploadsTableConfig = createTableConfiguration<
  ClientEmployeeFileUploadFieldsFragment,
  ClientEmployeeFileUploadTableColumns
>({
  tableId: "client-employee-file-uploads-v2",
  columns: {
    [ClientEmployeeFileUploadTableColumns.fileName]: {
      id: ClientEmployeeFileUploadTableColumns.fileName,
      label:
        CLIENT_EMPLOYEE_FILE_UPLOAD_TABLE_COLUMN_LABELS[
          ClientEmployeeFileUploadTableColumns.fileName
        ],
      fieldPath: "fileName",
      sortField: "file_name",
      type: "custom",
      defaultVisible: true,
      render: (value: unknown) => {
        return renderFileName(value);
      },
    },
    [ClientEmployeeFileUploadTableColumns.application]: {
      id: ClientEmployeeFileUploadTableColumns.application,
      label:
        CLIENT_EMPLOYEE_FILE_UPLOAD_TABLE_COLUMN_LABELS[
          ClientEmployeeFileUploadTableColumns.application
        ],
      fieldPath: "application.resource.publicResource.productName",
      sortField: "application__resource__public_resource__product_name",
      type: "custom",
      defaultVisible: false,
      render: (_value: unknown, row: ClientEmployeeFileUploadFieldsFragment) => {
        return renderApplication(row);
      },
    },
    [ClientEmployeeFileUploadTableColumns.employee]: {
      id: ClientEmployeeFileUploadTableColumns.employee,
      label:
        CLIENT_EMPLOYEE_FILE_UPLOAD_TABLE_COLUMN_LABELS[
          ClientEmployeeFileUploadTableColumns.employee
        ],
      fieldPath: "employee.name",
      sortField: "employee__name",
      type: "custom",
      defaultVisible: true,
      render: (_value: unknown, row: ClientEmployeeFileUploadFieldsFragment) => {
        return renderEmployee(row);
      },
    },
    [ClientEmployeeFileUploadTableColumns.mimetype]: {
      id: ClientEmployeeFileUploadTableColumns.mimetype,
      label:
        CLIENT_EMPLOYEE_FILE_UPLOAD_TABLE_COLUMN_LABELS[
          ClientEmployeeFileUploadTableColumns.mimetype
        ],
      fieldPath: "mimetype",
      sortField: "mimetype",
      type: "custom",
      defaultVisible: false,
      render: (value: unknown) => {
        return renderMimetype(value);
      },
    },
    [ClientEmployeeFileUploadTableColumns.uploadSize]: {
      id: ClientEmployeeFileUploadTableColumns.uploadSize,
      label:
        CLIENT_EMPLOYEE_FILE_UPLOAD_TABLE_COLUMN_LABELS[
          ClientEmployeeFileUploadTableColumns.uploadSize
        ],
      fieldPath: "uploadSize",
      sortField: "upload_size",
      type: "custom",
      defaultVisible: true,
      render: (value: unknown) => {
        return renderUploadSize(value);
      },
    },
    [ClientEmployeeFileUploadTableColumns.datetime]: {
      id: ClientEmployeeFileUploadTableColumns.datetime,
      label:
        CLIENT_EMPLOYEE_FILE_UPLOAD_TABLE_COLUMN_LABELS[
          ClientEmployeeFileUploadTableColumns.datetime
        ],
      fieldPath: "datetime",
      sortField: "datetime",
      type: "date",
      defaultVisible: true,
      render: (value: unknown) => {
        return renderDatetime(value);
      },
    },
  },
  defaultColumnOrder: DEFAULT_CLIENT_EMPLOYEE_FILE_UPLOAD_TABLE_COLUMN_ORDER,
  defaultColumnSettings: DEFAULT_CLIENT_EMPLOYEE_FILE_UPLOAD_TABLE_COLUMN_SETTINGS,
});

export const getDefaultFilterValues = () => ({
  ...DEFAULT_CLIENT_EMPLOYEE_FILE_UPLOADS_FILTER_VALUES,
});

export const areFiltersActive = (filterValues: {
  fileNameSearch: string;
  mimetypeSearch: string;
}): boolean => {
  return Boolean(
    (filterValues.fileNameSearch && filterValues.fileNameSearch.trim()) ||
      (filterValues.mimetypeSearch && filterValues.mimetypeSearch.trim()),
  );
};

export function renderFileName(value: unknown): React.ReactNode {
  return <Text truncate>{String(value || "-")}</Text>;
}

export function renderApplication(
  row: ClientEmployeeFileUploadFieldsFragment,
): React.ReactNode {
  if (!row.application?.resource?.publicResource) {
    return <Text color="textSecondary">-</Text>;
  }

  const { productName, vendorName, signedLogoUrl } =
    row.application.resource.publicResource;

  return (
    <Table.CompanyCell
      name={productName || vendorName || "Unknown Application"}
      signedLogoUrl={signedLogoUrl || undefined}
    />
  );
}

export function renderEmployee(
  row: ClientEmployeeFileUploadFieldsFragment,
): React.ReactNode {
  if (!row.employee) {
    return <Text color="textSecondary">-</Text>;
  }
  return <Table.UserCell name={row.employee.name} email={row.employee.email} />;
}

export function renderMimetype(value: unknown): React.ReactNode {
  return <Text truncate>{String(value || "-")}</Text>;
}

export function renderUploadSize(value: unknown): React.ReactNode {
  const size = Number(value);
  if (isNaN(size)) {
    return <Text color="textSecondary">-</Text>;
  }
  return <Text>{formatBytes(size)}</Text>;
}

export function renderDatetime(value: unknown): React.ReactNode {
  if (!value) return <Text color="textSecondary">-</Text>;
  return <Table.DateCell date={new Date(value as string)} />;
}
